import type { ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { returnPathAfterSignIn, SignInForm } from '@/features/auth';
import { BrandLogo } from '@/shared/ui';
import { AdminAuthPanel } from '@/widgets/admin-auth-panel';

/**
 * 관리자 로그인 — 화면 전체를 쓰는 두 칸(넓은 화면): 왼쪽 흑연 브랜드 면, 오른쪽 폼. 좁은 화면은 폼만 쌓는다.
 * 성공하면 가드가 넘겨준 원래 목적지(앱 안 경로)로, 없으면 첫 화면으로 간다.
 */
export const SignInPage = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    // 한국어는 어절 단위로 줄을 바꾼다 — 고객 웹과 같은 규칙.
    <div className="grid min-h-dvh bg-background break-keep lg:grid-cols-[1.1fr_1fr]">
      <div className="sticky top-0 hidden h-dvh lg:block">
        <AdminAuthPanel />
      </div>

      <section className="flex flex-col px-5 py-6 sm:px-8 xl:px-14">
        {/* 넓은 화면은 왼쪽 브랜드 면에 로고가 있어 좁은 화면에서만 보인다. */}
        <BrandLogo className="h-7 self-start sm:h-8 lg:hidden" />

        <div className="flex flex-1 items-center justify-center py-12 sm:py-16">
          <div className="w-full max-w-md">
            <header className="flex flex-col gap-3">
              <p
                aria-hidden
                className="text-xs font-semibold tracking-[0.12em] text-muted-foreground"
              >
                ADMIN
              </p>
              {/* 보이는 제목은 고객 웹처럼 영문 한 단어, 보조기기에는 한국어 화면 이름을 읽힌다. */}
              <h1 className="text-[clamp(2.5rem,5vw,3.5rem)] leading-none font-bold tracking-[-0.03em]">
                <span aria-hidden>Sign in</span>
                <span className="sr-only">관리자 로그인</span>
              </h1>
              <p className="text-[15px] text-muted-foreground">관리자 계정으로 로그인하세요.</p>
            </header>

            <div className="mt-10">
              <SignInForm
                onSuccess={() =>
                  void navigate(returnPathAfterSignIn(location.state), { replace: true })
                }
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
